"use client";
import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";

import { tagLabel } from "./browse-values";
import type { CoworldTile } from "./browse-records";
import { renderNativeLink, type BrowseLinkRenderer } from "./browse-link";

export function BrowseTile({
  tile,
  renderLink = renderNativeLink,
}: {
  readonly tile: CoworldTile;
  readonly renderLink?: BrowseLinkRenderer;
}) {
  const { row, figures, continuation, blurb, media } = tile;
  const body = (
    <>
      {media}
      <span {...stylex.props(styles.tileNameLine)}>
        <span {...stylex.props(styles.tileName)}>{row.name}</span>
      </span>
      {blurb ? <span {...stylex.props(styles.tileBlurb)}>{blurb}</span> : null}
      {figures}
      {row.tags.length ? (
        <span {...stylex.props(styles.tags)}>
          {row.tags.map((tag) => (
            <span key={tag} {...stylex.props(styles.tag)}>
              {tagLabel(tag)}
            </span>
          ))}
        </span>
      ) : null}
    </>
  );
  return (
    <li {...stylex.props(styles.tileItem)}>
      {row.href ? (
        renderLink({
          href: row.href,
          ...stylex.props(styles.tileLink),
          "aria-label": row.name,
          children: body,
        })
      ) : (
        <span {...stylex.props(styles.tileLink)}>{body}</span>
      )}
      <div {...stylex.props(styles.tileContinuation)}>{continuation}</div>
    </li>
  );
}

const styles = stylex.create({
  tileItem: { minWidth: 0 },
  tileLink: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.space16,
    borderRadius: radii.small,
    margin: `calc(-1 * ${spacing.space12})`,
    padding: spacing.space12,
    color: "inherit",
    textDecoration: "none",
    backgroundColor: { default: "transparent", ":hover": colors.hover },
    outline: {
      default: "none",
      ":focus-visible": `2px solid ${colors.foreground}`,
    },
    outlineOffset: 2,
  },
  tileNameLine: { display: "flex", alignItems: "center", gap: spacing.space12 },
  tileName: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: fonts.mono,
    fontSize: fontSizes.title,
    lineHeight: 1.3,
    fontWeight: 500,
  },
  tileBlurb: {
    fontFamily: fonts.sans,
    fontSize: fontSizes.reading,
    lineHeight: 1.5,
    color: colors.secondary,
  },
  tileContinuation: { minHeight: spacing.space32, marginTop: spacing.space12 },
  tags: { display: "flex", flexWrap: "wrap", gap: spacing.space6 },
  tag: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.borderStrong,
    borderRadius: 3,
    paddingInline: spacing.space8,
    paddingBlock: spacing.space4,
    fontFamily: fonts.sans,
    fontSize: fontSizes.caption,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
});
